import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Typography,
  IconButton
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, Share as ShareIcon } from '@mui/icons-material';
import axiosInstance from '../config/axios';
import { FRONTEND_URL } from '../config/constants';

const CustomerQR: React.FC = () => {
  const [qrDialogOpen, setQrDialogOpen] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [tempLink, setTempLink] = useState<string>('');
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [remainingTime, setRemainingTime] = useState<string>('00:00');
  const [notification, setNotification] = useState<{open: boolean, message: string, type: 'success' | 'error'}>({ open: false, message: '', type: 'success' });
  const startedRef = useRef(false);

  // Link oluştur ve modalı aç (sayfa yüklenince)
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    axiosInstance.post('/api/v1/Customer/create-temp-link', { customerCode: '' })
      .then((response) => {
        // API yanıtı farklı formatlarda gelebilir: data.data.tempLink veya data.tempLink
        let tempLinkValue = '';
        let minutesValue = 10;

        if (response.data && response.data.data) {
          tempLinkValue = response.data.data.tempLink;
          minutesValue = response.data.data.expiryMinutes ?? 10;
        } else if (response.data) {
          tempLinkValue = response.data.tempLink;
          minutesValue = response.data.expiryMinutes ?? 10;
        }

        // localhost ise FRONTEND_URL ile değiştir
        if (tempLinkValue && tempLinkValue.includes('localhost')) {
          tempLinkValue = tempLinkValue.replace(/https?:\/\/localhost(:[0-9]+)?/, FRONTEND_URL);
        }
        // Origin FRONTEND_URL'den farklıysa normalize et
        try {
          if (tempLinkValue) {
            const incoming = new URL(tempLinkValue);
            const target = new URL(FRONTEND_URL);
            if (incoming.origin !== target.origin) {
              tempLinkValue = `${target.origin}${incoming.pathname}${incoming.search}${incoming.hash}`;
            }
          }
        } catch {}

        setTempLink(tempLinkValue);

        const qrUrl = tempLinkValue
          ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(tempLinkValue)}`
          : '';
        setQrCodeUrl(qrUrl);

        const expiry = new Date();
        expiry.setMinutes(expiry.getMinutes() + (Number(minutesValue) || 10));
        setExpiryTime(expiry);
      })
      .catch((err) => {
        console.error('Geçici link oluşturma hatası:', err);
        setNotification({ open: true, message: 'Geçici link oluşturulurken bir hata oluştu!', type: 'error' });
      });
  }, []);

  // Sayaç
  useEffect(() => {
    const interval = setInterval(() => {
      if (!expiryTime) return;
      const now = new Date();
      const diffMs = expiryTime.getTime() - now.getTime();
      if (diffMs <= 0) {
        setRemainingTime('00:00');
        clearInterval(interval);
        return;
      }
      const mins = Math.floor(diffMs / 60000);
      const secs = Math.floor((diffMs % 60000) / 1000);
      setRemainingTime(`${mins < 10 ? '0' + mins : mins}:${secs < 10 ? '0' + secs : secs}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [expiryTime]);

  const copyToClipboard = () => {
    if (!tempLink) return;
    navigator.clipboard.writeText(tempLink)
      .then(() => setNotification({ open: true, message: 'Link panoya kopyalandı!', type: 'success' }))
      .catch(() => setNotification({ open: true, message: 'Link kopyalanırken bir hata oluştu!', type: 'error' }));
  };

  const shareLink = async () => {
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({ title: 'Müşteri Kayıt Linki', url: tempLink });
      } else {
        copyToClipboard();
      }
    } catch (e) {
      copyToClipboard();
    }
  };

  const handleClose = () => {
    setQrDialogOpen(false);
    // İsteğe bağlı: otomatik geri dön veya farklı sayfaya yönlendir
    // window.history.back();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Dialog open={qrDialogOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Geçici Müşteri Kayıt Linki</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2, py: 1 }}>
            {qrCodeUrl ? (
              <img src={qrCodeUrl} alt="QR Code" style={{ width: 200, height: 200 }} />
            ) : (
              <Typography variant="body2" color="text.secondary">QR hazırlanıyor...</Typography>
            )}
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{tempLink || 'Link hazırlanıyor...'}</Typography>
            <Typography variant="caption" color="text.secondary">Geçerlilik: {remainingTime}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyToClipboard}>Kopyala</Button>
              <Button variant="contained" startIcon={<ShareIcon />} onClick={shareLink}>Paylaş</Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={notification.open} autoHideDuration={3000} onClose={() => setNotification(p => ({ ...p, open: false }))}>
        <Alert onClose={() => setNotification(p => ({ ...p, open: false }))} severity={notification.type} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerQR;
